import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, Eye, EyeOff, ArrowRight, CheckCircle2 } from "lucide-react";

import Logo from "src/components/common/Logo";
import Input from "src/components/ui/Input";
import Button from "src/components/ui/Button";
import AuthThemeToggle from "src/components/auth/AuthThemeToggle";

import * as authTenantActions from "src/redux/slices/authTenantSlice";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const { resetPassword } = useAppSelector((state) => state.authTenant);

  const email = searchParams.get("email") || "";
  const token = searchParams.get("token") || "";

  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [apiError, setApiError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (resetPassword?.data && !resetPassword?.error) {
      setDone(true);
    } else if (resetPassword?.error) {
      setApiError(resetPassword.error);
    }
  }, [resetPassword]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setApiError("");
    dispatch(
      authTenantActions.resetPasswordRequest({ email, token, newPassword })
    );
  };

  const missingLink = !email || !token;

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6 dark:bg-gray-900">
      <AuthThemeToggle />
      <div className="w-full max-w-[380px]">
        <div className="rounded-[18px] border border-gray-200 bg-white p-8 text-center shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-5 flex items-center justify-center gap-2">
            <Logo size="small" variant="contained" />
            <Logo size="large" variant="text" />
          </div>

          {missingLink ? (
            <>
              <h2 className="mb-1.5 text-xl font-extrabold text-gray-900 dark:text-gray-100">
                Invalid reset link
              </h2>
              <p className="mb-6 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
                This link is missing or malformed. Request a new one instead.
              </p>
              <Link to="/app/forgot-password">
                <Button fullWidth>Request a new link</Button>
              </Link>
            </>
          ) : done ? (
            <>
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
                <CheckCircle2 size={26} />
              </div>
              <h2 className="mb-1.5 text-xl font-extrabold text-gray-900 dark:text-gray-100">
                Password updated
              </h2>
              <p className="mb-6 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
                You can now sign in with your new password.
              </p>
              <Button fullWidth onClick={() => navigate("/app/login")}>
                Sign in
              </Button>
            </>
          ) : (
            <>
              <h2 className="mb-1.5 text-xl font-extrabold text-gray-900 dark:text-gray-100">
                Set a new password
              </h2>
              <p className="mb-6 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
                Choose a new password for <strong>{email}</strong>.
              </p>

              {apiError && (
                <div className="mb-4 rounded-lg bg-red-100 p-2.5 text-center text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">
                  {apiError}
                </div>
              )}

              <form className="space-y-3.5 text-left" onSubmit={handleSubmit}>
                <Input
                  label="New password"
                  required
                  type={showPassword ? "text" : "password"}
                  placeholder="At least 8 characters"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  startAdornment={<Lock size={16} />}
                  endAdornment={
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      className="flex items-center"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  }
                />
                <Button
                  type="submit"
                  fullWidth
                  isLoading={resetPassword?.loading}
                  className="mt-1.5"
                >
                  Update password
                  <ArrowRight size={15} className="ml-1.5" />
                </Button>
              </form>
            </>
          )}

          <p className="mt-4.5 text-sm text-gray-500 dark:text-gray-400">
            <Link
              to="/app/login"
              className="font-semibold text-primary-600 hover:underline dark:text-primary-400"
            >
              Back to sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
